import { Icon } from '@/shared/ui/Icon'
import { cn } from '@/shared/utils/cn'
import { useCopyToClipboard } from '@/shared/utils/useCopyToClipboard'

interface Props {
  /** Raw, already-de-indented code text. */
  value: string
  /** Fenced-block language (``js`` from ```` ```js ````), if any. */
  language?: string
}

/**
 * POLISH 1 — grey "code card" for markdown fenced/indented code blocks.
 *
 * - Distinct dark/grey surface with a slim header (language + copy).
 * - Whitespace is preserved and the body scrolls horizontally on overflow so
 *   long lines never widen the chat column.
 * - The copy button stops click propagation so it never triggers the
 *   answer-bubble "select message" handler underneath it.
 */
export function CodeBlock({ value, language }: Props) {
  const { copied, copy } = useCopyToClipboard()
  const label = (language ?? '').trim()

  return (
    <div className="code-block" data-testid="code-block">
      <div className="code-block-head">
        {label ? (
          <span className="code-block-lang">{label}</span>
        ) : (
          <span className="code-block-lang code-block-lang--muted">código</span>
        )}
        <button
          type="button"
          className={cn('code-block-copy', { copied })}
          title={copied ? 'Copiado' : 'Copiar código'}
          aria-label={copied ? 'Código copiado' : 'Copiar código'}
          onClick={(e) => {
            e.stopPropagation()
            void copy(value)
          }}
        >
          <Icon name={copied ? 'check' : 'copy'} />
          <span>{copied ? 'Copiado' : 'Copiar'}</span>
        </button>
      </div>
      <pre className="code-block-pre">
        <code>{value}</code>
      </pre>
    </div>
  )
}
